import { Box, CircularProgress, Typography } from '@mui/material'
import { PictureAsPdfOutlined as PdfIcon } from '@mui/icons-material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { typeScale } from '@/shared/styles/typography'

import { type SendingItem } from './attachmentUploads'
import { openPdf } from './openPdf'
import { isPdfUrl, type RecordingImage } from './recordingImageApi'

// 栄養の作る前の panel の 96px のタイル (食事の写真が複数並ぶ面)。薬剤師と nasu の一覧は 1 件 1 行 (AttachmentRows)
// (design-book 02-components/attachments-panel.md「AttachmentsPanel」)。

export const TILE = 96

// タイルの中の文字の釦 (もう一度・端末に保存)
const tileActionSx = {
  ...typeScale.micro,
  color: colorTokens.fg.muted,
  p: 0,
  border: 'none',
  bgcolor: 'transparent',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { color: colorTokens.fg.default },
} as const

const tileSx = {
  width: TILE,
  height: TILE,
  borderRadius: `${RADIUS.md}px`,
  bgcolor: colorTokens.bg.subtle,
  border: `1px solid ${colorTokens.border.default}`,
} as const

interface TileProps {
  image: RecordingImage
  // 写真を押すと拡大。PDF は文書の絵と「PDF」で、押すと新しいタブ
  onOpen: () => void
  alt?: string
  // ✕ など、タイルに重ねるもの
  children?: React.ReactNode
}

export const AttachmentTile = ({
  image,
  onOpen,
  alt = '資料',
  children,
}: TileProps) => (
  <Box sx={{ position: 'relative', width: TILE, height: TILE }}>
    {isPdfUrl(image.image) ? (
      <Box
        component="button"
        type="button"
        aria-label="PDF を開く"
        onClick={() => openPdf(image.image)}
        sx={{
          ...tileSx,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          color: colorTokens.fg.muted,
          cursor: 'pointer',
          p: 0,
          '&:hover': { color: colorTokens.fg.default },
        }}
      >
        <PdfIcon sx={{ fontSize: 28 }} />
        <Typography sx={{ fontSize: 11 }}>PDF</Typography>
      </Box>
    ) : (
      <Box
        component="img"
        src={image.image}
        alt={alt}
        onClick={onOpen}
        sx={{
          ...tileSx,
          display: 'block',
          objectFit: 'cover',
          cursor: 'pointer',
        }}
      />
    )}
    {children}
  </Box>
)

interface SendingTileProps {
  item: SendingItem
  onRetry: () => void
  onSave: () => void
}

// 送信中は spinner、送れなかったら「もう一度」「端末に保存」の 2 つの手
export const SendingTile = ({ item, onRetry, onSave }: SendingTileProps) => {
  const sx = {
    ...tileSx,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    color: colorTokens.fg.muted,
  } as const
  if (item.status === 'sending') {
    return (
      <Box role="status" aria-label="送信中の資料" sx={sx}>
        <CircularProgress size={20} sx={{ color: 'inherit' }} />
      </Box>
    )
  }
  return (
    <Box role="group" aria-label="送れなかった資料" sx={sx}>
      <Box component="button" type="button" onClick={onRetry} sx={tileActionSx}>
        もう一度
      </Box>
      <Box component="button" type="button" onClick={onSave} sx={tileActionSx}>
        端末に保存
      </Box>
    </Box>
  )
}
